import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leadsService } from '../../services/leads.service'
import toast from 'react-hot-toast'

export default function SendQueuePanel() {
  const queryClient = useQueryClient()

  const { data: queue } = useQuery({
    queryKey: ['sendQueue'],
    queryFn: () => leadsService.getSendQueue(),
    refetchInterval: (query) => {
      const d = query.state.data
      if (!d) return false
      // Poll while there are active messages
      return d.total_queued > 0 || d.total_sending > 0 || d.total_paused > 0
        ? 3000
        : false
    },
  })

  const pauseMutation = useMutation({
    mutationFn: () => leadsService.pauseSending(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sendQueue'] })
      toast.success(data.message)
    },
    onError: () => toast.error('Ошибка при остановке'),
  })

  const resumeMutation = useMutation({
    mutationFn: () => leadsService.resumeSending(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sendQueue'] })
      toast.success(data.message)
    },
    onError: () => toast.error('Ошибка при возобновлении'),
  })

  const cancelMutation = useMutation({
    mutationFn: () => leadsService.cancelSending(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sendQueue'] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      toast.success(data.message)
    },
    onError: () => toast.error('Ошибка при отмене'),
  })

  if (!queue) return null

  const total = queue.total_queued + queue.total_sending + queue.total_sent + queue.total_failed + queue.total_paused
  if (total === 0) return null

  const progress = total > 0 ? Math.round((queue.total_sent / total) * 100) : 0
  const isActive = queue.total_queued > 0 || queue.total_sending > 0 || queue.total_paused > 0

  const statusBadge = (status: string) => {
    const map: Record<string, { bg: string; text: string; label: string }> = {
      queued: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'В очереди' },
      sending: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Отправка...' },
      sent: { bg: 'bg-green-100', text: 'text-green-700', label: 'Отправлено' },
      failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Ошибка' },
      paused: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Пауза' },
    }
    const s = map[status] || { bg: 'bg-gray-100', text: 'text-gray-600', label: status }
    return (
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s.bg} ${s.text}`}>
        {s.label}
      </span>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Рассылка сообщений
        </h3>
        <div className="flex items-center gap-2">
          {isActive && !queue.is_paused && (
            <button
              onClick={() => pauseMutation.mutate()}
              disabled={pauseMutation.isPending}
              className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {pauseMutation.isPending ? '...' : 'Стоп'}
            </button>
          )}
          {queue.is_paused && (
            <button
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isPending}
              className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {resumeMutation.isPending ? '...' : 'Продолжить'}
            </button>
          )}
          {isActive && (
            <button
              onClick={() => {
                if (window.confirm('Отменить все неотправленные сообщения?')) {
                  cancelMutation.mutate()
                }
              }}
              disabled={cancelMutation.isPending}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Отменить всё
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div
          className="bg-green-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Counters */}
      <div className="flex items-center gap-4 text-xs mb-3">
        <span className="text-green-600 font-medium">
          Отправлено: {queue.total_sent}
        </span>
        <span className="text-yellow-600">
          В очереди: {queue.total_queued}
        </span>
        {queue.total_sending > 0 && (
          <span className="text-blue-600">
            Отправляется: {queue.total_sending}
          </span>
        )}
        {queue.total_paused > 0 && (
          <span className="text-gray-600">
            На паузе: {queue.total_paused}
          </span>
        )}
        {queue.total_failed > 0 && (
          <span className="text-red-600">
            Ошибки: {queue.total_failed}
          </span>
        )}
      </div>

      {/* Message list (compact) */}
      {queue.messages.length > 0 && (
        <div className="max-h-40 overflow-y-auto border-t border-gray-100 pt-2">
          <div className="space-y-1">
            {queue.messages.slice(0, 20).map((msg) => (
              <div
                key={msg.id}
                className="flex items-center justify-between text-xs py-1"
              >
                <span className="text-gray-700 truncate mr-2">
                  {msg.lead_username ? `@${msg.lead_username}` : `Lead #${msg.lead_id}`}
                </span>
                <div className="flex items-center gap-2">
                  {statusBadge(msg.send_status)}
                  {msg.error_text && (
                    <span className="text-red-500 truncate max-w-[150px]" title={msg.error_text}>
                      {msg.error_text}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {queue.messages.length > 20 && (
              <p className="text-xs text-gray-400 pt-1">
                ...и ещё {queue.messages.length - 20}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
